import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { getApiBaseUrl } from '../../../../core/config/api';
import { canPanelManagePremium } from '../../panelAccess/panelRoleAccess';

type TabKey = 'buildings' | 'vehicles' | 'staff' | 'interventions';
const TAB_LABELS: Record<TabKey, string> = {
  buildings: 'Bâtiments',
  vehicles: 'Véhicules',
  staff: 'Personnel',
  interventions: 'Interventions',
};

type PossessionData = { buildings: any[]; vehicles: any[]; staff: any[]; interventions: any[] };

const getVehicleIcon = (code?: string): string => {
  const c = String(code || '').toUpperCase();
  if (c.includes('VSAV') || c.includes('SUAP')) return '🚑';
  if (c.includes('CDG') || c.includes('VLC')) return '🚓';
  if (c.includes('FPT') || c.includes('EPA') || c.includes('CCR')) return '🚒';
  return '🚐';
};

const getStaffIcon = (gradeCode?: string): string => {
  const g = String(gradeCode || '').toUpperCase();
  if (g.includes('LTN') || g.includes('CNE')) return '🧑‍✈️';
  if (g.includes('SGT') || g.includes('ADJ')) return '🧑‍🚒';
  return '👨‍🚒';
};

const isCtaBuilding = (b: any): boolean =>
  String(b?.building_code || '').toLowerCase() === 'cta' || String(b?.building_type_name || '').toLowerCase().includes('cta');

const FieldWithIcon: React.FC<{
  icon: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  className?: string;
  readOnly?: boolean;
}> = ({ icon, label, value, placeholder, onChange, className, readOnly }) => (
  <label
    className={`flex min-w-0 items-center gap-2 rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100 ${className || ''}`}
  >
    <span className="shrink-0 text-sm">{icon}</span>
    <span className="shrink-0 text-[11px] text-slate-300">{label}</span>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      readOnly={readOnly}
      className="min-w-0 flex-1 bg-transparent text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none"
    />
  </label>
);

const PanelModerationManageV1: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ userId: string }>();
  const userId = Number(params.userId || 0);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const canManagePremium = canPanelManagePremium(currentUser?.roles_id ?? 99);
  const [tab, setTab] = React.useState<TabKey>('buildings');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [possessionData, setPossessionData] = React.useState<PossessionData | null>(null);
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editForm, setEditForm] = React.useState<Record<string, string>>({});
  const [searchByTab, setSearchByTab] = React.useState<Record<TabKey, string>>({
    buildings: '',
    vehicles: '',
    staff: '',
    interventions: '',
  });
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [economyAdjusting, setEconomyAdjusting] = React.useState(false);
  const [economyAction, setEconomyAction] = React.useState<{
    argents: { amount: string; reason: string };
    medailles: { amount: string; reason: string };
  }>({
    argents: { amount: '', reason: '' },
    medailles: { amount: '', reason: '' },
  });
  const [playerProfile, setPlayerProfile] = React.useState<{
    id: number;
    username: string;
    email: string;
    roles_id: number;
    activated: number;
    argents: number;
    medailles: number;
    cta_prise_appels?: number;
    premium?: number;
    xp?: number;
    supports_premium?: number;
    supports_xp?: number;
  } | null>(null);
  const [ctaScenarios, setCtaScenarios] = React.useState<Array<{ id: number; scenario_name?: string }>>([]);
  const [ctaBuildings, setCtaBuildings] = React.useState<Array<{ id: number; nom: string; actif: number }>>([]);
  const [buildingCtaLink, setBuildingCtaLink] = React.useState<Record<number, string>>({});
  const [generatedScenarioId, setGeneratedScenarioId] = React.useState<string>('');
  const [lookup, setLookup] = React.useState<{
    vehicle_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
    staff_statuses: Array<{ id: number; code: string; nom: string; disponible?: number }>;
    config_personnel: Array<{ id: number; grade: string; grade_code: string; ordre?: number }>;
    config_vehicles: Array<{ id: number; code: string; nom: string; specialiste?: number }>;
    planning_statuses: string[];
  }>({ vehicle_statuses: [], staff_statuses: [], config_personnel: [], config_vehicles: [], planning_statuses: [] });

  const items = React.useMemo(() => (possessionData ? possessionData[tab] : []) || [], [possessionData, tab]);
  const buildingOptions = React.useMemo(() => possessionData?.buildings || [], [possessionData]);
  const searchTerm = React.useMemo(() => String(searchByTab[tab] || '').trim().toLowerCase(), [searchByTab, tab]);
  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items;
    return items.filter((item: any) => {
      const haystack = [
        item?.id,
        item?.nom,
        item?.prenom,
        item?.numero_intervention,
        item?.adresse,
        item?.building_type_name,
        item?.vehicle_type_name,
        item?.vehicle_code,
        item?.personnel_grade,
        item?.personnel_grade_code,
        item?.staff_status_nom,
        item?.vehicle_status_nom,
        item?.planning_statut_nom,
        item?.statut,
        item?.batiment_nom,
      ]
        .map((x) => String(x ?? ''))
        .join(' ')
        .toLowerCase();
      return haystack.includes(searchTerm);
    });
  }, [items, searchTerm]);
  const vehicleStatusCodeById = React.useMemo(() => {
    const map = new Map<number, string>();
    lookup.vehicle_statuses.forEach((s) => map.set(Number(s.id), String(s.code || '').toLowerCase()));
    return map;
  }, [lookup.vehicle_statuses]);
  const vehicleStatusCodeByName = React.useMemo(() => {
    const map = new Map<string, string>();
    lookup.vehicle_statuses.forEach((s) => {
      const nom = String(s.nom || '').trim().toLowerCase();
      if (nom) map.set(nom, String(s.code || '').toLowerCase());
    });
    return map;
  }, [lookup.vehicle_statuses]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement possessions');
      const p = data.data as PossessionData;
      setPossessionData({
        buildings: Array.isArray(p?.buildings) ? p.buildings : [],
        vehicles: Array.isArray(p?.vehicles) ? p.vehicles : [],
        staff: Array.isArray(p?.staff) ? p.staff : [],
        interventions: Array.isArray(p?.interventions) ? p.interventions : [],
      });
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadProfile = React.useCallback(async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur chargement profil');
      setPlayerProfile(data.data || null);
    } catch (e: any) {
      setError(e?.message || 'Erreur chargement profil');
    }
  }, [userId]);

  const loadLookupRefs = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/lookup-refs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setLookup(
        data.data || {
          vehicle_statuses: [],
          staff_statuses: [],
          config_personnel: [],
          config_vehicles: [],
          planning_statuses: [],
        },
      );
    } catch {
      // ignore
    }
  }, []);

  const loadCtaScenarios = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/cta/scenarios?page=1&limit=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setCtaScenarios(Array.isArray(data.data) ? data.data : []);
    } catch {
      // ignore
    }
  }, []);

  const loadCtaBuildings = React.useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/cta-buildings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) return;
      setCtaBuildings(Array.isArray(data.data) ? data.data : []);
    } catch {
      // ignore
    }
  }, [userId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  React.useEffect(() => {
    void loadLookupRefs();
  }, [loadLookupRefs]);

  React.useEffect(() => {
    if (tab === 'interventions') void loadCtaScenarios();
  }, [tab, loadCtaScenarios]);

  React.useEffect(() => {
    if (tab === 'buildings') void loadCtaBuildings();
  }, [tab, loadCtaBuildings]);

  const handleDelete = async (t: TabKey, ownerId: number, id: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${ownerId}/${t}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur suppression');
      setEditingId(null);
      setEditForm({});
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur suppression');
    }
  };

  const callAction = async (url: string, method: 'PATCH' | 'POST', body?: Record<string, unknown>) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('Token manquant');
    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) throw new Error(data?.error || 'Action impossible');
  };

  const startEdit = (item: any) => {
    setEditingId(Number(item.id));
    const next: Record<string, string> = {};
    if (tab === 'buildings') {
      next.nom = String(item.nom || '');
      next.niveau = String(item.niveau || 1);
    } else if (tab === 'vehicles') {
      next.nom = String(item.nom || '');
      next.capacite = String(item.capacite ?? 0);
      next.kilometrage = String(item.kilometrage ?? 0);
      next.vehicle_status_id = String(item.vehicle_status_id || '');
      next.batiment_id = String(item.batiment_id || '');
      next.premium = String(Number(item.premium || 0));
      next.cout = String(item.cout ?? 0);
      next.config_vehicle_id = String(item.config_vehicle_id || '');
    } else if (tab === 'staff') {
      next.prenom = String(item.prenom || '');
      next.nom = String(item.nom || '');
      next.config_personnel_id = String(item.config_personnel_id || '');
      next.staff_status_id = String(item.staff_status_id || '');
      next.staff_status_code = String(
        vehicleStatusCodeById.get(Number(item.vehicle_status_id || item.staff_status_id || 0)) ||
          vehicleStatusCodeByName.get(String(item.staff_status_nom || '').trim().toLowerCase()) ||
          vehicleStatusCodeById.get(Number(item.staff_status_id || 0)) ||
          '',
      );
      next.planning_statut_nom = String(item.planning_statut_nom || '');
      next.batiment_id = String(item.batiment_id || '');
      next.actif = String(Number(item.actif ?? 1));
      next.premium = String(Number(item.premium || 0));
      next.groupe = String(item.groupe || '');
    } else {
      next.numero_intervention = String(item.numero_intervention || '');
      next.adresse = String(item.adresse || '');
      next.statut = String(item.statut || 'DISPONIBLE');
    }
    setEditForm(next);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (ownerId: number, item: any) => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const id = Number(item?.id || 0);
      const base = `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${ownerId}`;

      if (tab === 'buildings') {
        const res = await fetch(`${base}/buildings/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nom: editForm.nom || item.nom, niveau: Number(editForm.niveau || 1) }),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else if (tab === 'vehicles') {
        const body: Record<string, unknown> = {
          nom: (editForm.nom || '').trim() || item.nom,
          capacite: Number(editForm.capacite ?? 0),
          kilometrage: Number(editForm.kilometrage ?? 0),
          vehicle_status_id: Number(editForm.vehicle_status_id || 0) || undefined,
          batiment_id: editForm.batiment_id === '' ? 0 : Number(editForm.batiment_id || 0),
          premium: Number(editForm.premium || 0) ? 1 : 0,
          cout: Number(editForm.cout ?? 0),
        };
        const cvId = Number(editForm.config_vehicle_id);
        if (Number.isFinite(cvId) && cvId > 0) body.config_vehicle_id = cvId;
        const res = await fetch(`${base}/vehicles/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else if (tab === 'staff') {
        const body: Record<string, unknown> = {
          prenom: (editForm.prenom || '').trim(),
          nom: (editForm.nom || '').trim(),
          planning_statut_nom: (editForm.planning_statut_nom || '').trim(),
          staff_status_code: (editForm.staff_status_code || '').trim().toLowerCase() || undefined,
          batiment_id: editForm.batiment_id === '' ? 0 : Number(editForm.batiment_id || 0),
          config_personnel_id: Number(editForm.config_personnel_id || 0) || undefined,
          actif: Number(editForm.actif || 0) ? 1 : 0,
          premium: Number(editForm.premium || 0) ? 1 : 0,
          groupe: (editForm.groupe || '').trim() || null,
        };
        const res = await fetch(`${base}/staff/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      } else {
        if (!String(editForm.adresse || '').trim()) throw new Error('Adresse requise pour intervention');
        const res = await fetch(`${base}/interventions/${id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            numero_intervention: (editForm.numero_intervention || '').trim(),
            adresse: editForm.adresse,
            statut: editForm.statut,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde');
      }

      setEditingId(null);
      setEditForm({});
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur sauvegarde');
    }
  };

  const savePlayerProfile = async () => {
    if (!playerProfile) return;
    try {
      setSavingProfile(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const { premium: _premium, argents: _argents, medailles: _medailles, xp: _xp, ...profileSansEconomie } =
        playerProfile;
      const payload = canManagePremium ? playerProfile : profileSansEconomie;
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/profile`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur sauvegarde profil');
      await loadProfile();
    } catch (e: any) {
      setError(e?.message || 'Erreur profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const adjustEconomy = async (resource: 'argents' | 'medailles', operation: 'add' | 'remove') => {
    try {
      if (!playerProfile) return;
      setError(null);
      const amount = Math.floor(Number(economyAction[resource].amount || 0));
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Montant invalide (doit être > 0)');
      }
      setEconomyAdjusting(true);
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Token manquant');
      const res = await fetch(`${getApiBaseUrl(true)}/panel/v1/moderation/player/${userId}/economy-adjust`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource,
          operation,
          amount,
          reason: economyAction[resource].reason || '',
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || 'Erreur ajustement économie');
      setEconomyAction((prev) => ({
        ...prev,
        [resource]: { ...prev[resource], amount: '', reason: '' },
      }));
      await loadProfile();
    } catch (e: any) {
      setError(e?.message || 'Erreur ajustement économie');
    } finally {
      setEconomyAdjusting(false);
    }
  };

  const toggleBuildingActive = async (buildingId: number, nextActif: boolean) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/buildings/${buildingId}/toggle-active`,
        'PATCH',
        { actif: nextActif ? 1 : 0 },
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur activation bâtiment');
    }
  };

  const updateBuildingCta = async (buildingId: number, ctaIdValue: string) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/buildings/${buildingId}/cta-link`,
        'PATCH',
        { cta_id: Number(ctaIdValue || 0) || null },
      );
      await load();
      await loadCtaBuildings();
    } catch (e: any) {
      setError(e?.message || 'Erreur liaison CTA');
    }
  };

  const resetIntervention = async (interventionId: number) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/${interventionId}/reset`,
        'PATCH',
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur reset intervention');
    }
  };

  const cancelIntervention = async (interventionId: number) => {
    try {
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/${interventionId}/cancel`,
        'PATCH',
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur annulation intervention');
    }
  };

  const generateFromScenario = async () => {
    try {
      if (!generatedScenarioId) {
        setError('Scénario requis');
        return;
      }
      setError(null);
      await callAction(
        `${getApiBaseUrl(true)}/panel/v1/moderation/possessions/${userId}/interventions/generate-from-scenario`,
        'POST',
        { scenario_id: Number(generatedScenarioId) },
      );
      await load();
    } catch (e: any) {
      setError(e?.message || 'Erreur génération intervention');
    }
  };

  const thCls = 'border-b border-[#2b3a61] bg-[#0a1329] px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400';
  const tdCls = 'border-b border-[#263455] px-2 py-2 align-middle text-[11px] text-slate-200';

  return (
    <div className="rounded-lg border border-[#263455] bg-[#0b1734] p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Gestion possessions — joueur #{userId}</h2>
        <button
          type="button"
          onClick={() => navigate('/panel/moderation')}
          className="rounded bg-slate-700 px-3 py-1 text-xs text-white hover:bg-slate-600"
        >
          Retour
        </button>
      </div>

      {playerProfile && (
        <div className="mb-3 rounded border border-[#33456f] bg-[#101f44] p-3">
          <div className="mb-2 rounded border border-[#2b3a61] bg-[#0a1329] p-2">
            <div className="text-sm font-semibold text-white">
              Joueur ciblé : {playerProfile.username} (ID #{playerProfile.id})
            </div>
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-slate-300">
              <span>Email : {playerProfile.email}</span>
              <span>Activé : {Number(playerProfile.activated) ? 'Oui' : 'Non'}</span>
            </div>
          </div>
          <div className="mb-2 text-sm font-semibold text-white">
            Profil joueur {canManagePremium ? '(économie / statut / progression)' : '(modération — sans économie ni premium)'}
          </div>
          {!canManagePremium ? (
            <p className="mb-2 text-xs text-amber-200/90">
              Premium, argents, médailles et XP : réservés au CM et aux administrateurs. Utilisez la modération pour
              activer/désactiver le compte.
            </p>
          ) : null}
          <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
            <FieldWithIcon
              icon="🆔"
              label="ID joueur"
              value={String(playerProfile.id || '')}
              onChange={() => {}}
              placeholder="ID"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="👤"
              label="Pseudo"
              value={playerProfile.username || ''}
              onChange={(v) => setPlayerProfile((p) => (p ? { ...p, username: v } : p))}
              placeholder="Username"
            />
            <FieldWithIcon
              icon="✉️"
              label="Email"
              value={playerProfile.email || ''}
              onChange={(v) => setPlayerProfile((p) => (p ? { ...p, email: v } : p))}
              placeholder="Email"
            />
            <FieldWithIcon
              icon="💶"
              label="Argent"
              value={String(playerProfile.argents ?? 0)}
              onChange={() => {}}
              placeholder="Argent"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="🏅"
              label="Médailles"
              value={String(playerProfile.medailles ?? 0)}
              onChange={() => {}}
              placeholder="Médailles"
              className="opacity-70"
              readOnly
            />
            <FieldWithIcon
              icon="✅"
              label="Activé"
              value={String(playerProfile.activated ?? 1)}
              onChange={(v) => setPlayerProfile((p) => (p ? { ...p, activated: Number(v || 0) } : p))}
              placeholder="Activé (0/1)"
            />
            {canManagePremium && Number(playerProfile.supports_premium || 0) ? (
              <label className="flex items-center gap-2 rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100">
                <span className="text-sm">⭐</span>
                <span className="shrink-0 text-[11px] text-slate-300">Premium</span>
                <select
                  value={String(Number(playerProfile.premium || 0))}
                  onChange={(e) => setPlayerProfile((p) => (p ? { ...p, premium: Number(e.target.value || 0) } : p))}
                  className="w-full bg-transparent text-xs text-slate-100 focus:outline-none"
                >
                  <option value="0">Non</option>
                  <option value="1">Oui</option>
                </select>
              </label>
            ) : null}
            {canManagePremium && Number(playerProfile.supports_xp || 0) ? (
              <FieldWithIcon
                icon="📈"
                label="XP"
                value={String(playerProfile.xp ?? 0)}
                onChange={(v) => setPlayerProfile((p) => (p ? { ...p, xp: Number(v || 0) } : p))}
                placeholder="XP"
              />
            ) : null}
          </div>
          {canManagePremium ? (
          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="rounded border border-[#33456f] bg-[#0a1329] p-2">
              <div className="mb-1 text-xs font-semibold text-white">Ajustement Argent</div>
              <div className="grid grid-cols-1 gap-1">
                <input
                  type="number"
                  min={1}
                  value={economyAction.argents.amount}
                  onChange={(e) => setEconomyAction((prev) => ({ ...prev, argents: { ...prev.argents, amount: e.target.value } }))}
                  placeholder="Montant"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <input
                  value={economyAction.argents.reason}
                  onChange={(e) => setEconomyAction((prev) => ({ ...prev, argents: { ...prev.argents, reason: e.target.value } }))}
                  placeholder="Raison (optionnel, log audit)"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('argents', 'add')}
                    className="rounded bg-emerald-700 px-2 py-1 text-[11px] text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    + Ajouter
                  </button>
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('argents', 'remove')}
                    className="rounded bg-amber-700 px-2 py-1 text-[11px] text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    - Retirer
                  </button>
                </div>
              </div>
            </div>
            <div className="rounded border border-[#33456f] bg-[#0a1329] p-2">
              <div className="mb-1 text-xs font-semibold text-white">Ajustement Médailles</div>
              <div className="grid grid-cols-1 gap-1">
                <input
                  type="number"
                  min={1}
                  value={economyAction.medailles.amount}
                  onChange={(e) => setEconomyAction((prev) => ({ ...prev, medailles: { ...prev.medailles, amount: e.target.value } }))}
                  placeholder="Montant"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <input
                  value={economyAction.medailles.reason}
                  onChange={(e) => setEconomyAction((prev) => ({ ...prev, medailles: { ...prev.medailles, reason: e.target.value } }))}
                  placeholder="Raison (optionnel, log audit)"
                  className="rounded border border-[#33456f] bg-[#101f44] px-2 py-1 text-xs text-slate-100"
                />
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('medailles', 'add')}
                    className="rounded bg-emerald-700 px-2 py-1 text-[11px] text-white hover:bg-emerald-600 disabled:opacity-50"
                  >
                    + Ajouter
                  </button>
                  <button
                    type="button"
                    disabled={economyAdjusting}
                    onClick={() => void adjustEconomy('medailles', 'remove')}
                    className="rounded bg-amber-700 px-2 py-1 text-[11px] text-white hover:bg-amber-600 disabled:opacity-50"
                  >
                    - Retirer
                  </button>
                </div>
              </div>
            </div>
          </div>
          ) : null}
          <div className="mt-2">
            <button
              type="button"
              onClick={() => void savePlayerProfile()}
              disabled={savingProfile}
              className="rounded bg-emerald-600 px-3 py-1 text-xs text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {savingProfile ? 'Sauvegarde...' : 'Sauvegarder profil joueur'}
            </button>
          </div>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['buildings', 'vehicles', 'staff', 'interventions'] as TabKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              setTab(k);
              setEditingId(null);
              setEditForm({});
            }}
            className={`rounded px-3 py-1 text-xs ${tab === k ? 'bg-[#4b57ff] text-white' : 'bg-[#101f44] text-slate-200'}`}
          >
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="mb-3 flex items-center gap-2 rounded border border-[#33456f] bg-[#101f44] px-2 py-2">
        <span className="text-xs text-slate-300">Recherche ({TAB_LABELS[tab]})</span>
        <input
          value={searchByTab[tab]}
          onChange={(e) => setSearchByTab((prev) => ({ ...prev, [tab]: e.target.value }))}
          placeholder="Nom, ID, statut, adresse..."
          className="w-full min-w-[220px] rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500"
        />
        <span className="text-[11px] text-slate-500">
          {filteredItems.length}/{items.length}
        </span>
      </div>

      {tab === 'interventions' && (
        <div className="mb-3 rounded border border-[#33456f] bg-[#101f44] p-3 text-xs text-slate-200">
          <div className="mb-2 font-semibold text-white">Générer une intervention (scénario CTA — adresse aléatoire, comme &quot;tester sur mon compte&quot;)</div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              <span>Scénario</span>
              <select
                value={generatedScenarioId}
                onChange={(e) => setGeneratedScenarioId(e.target.value)}
                className="min-w-[280px] rounded border border-[#33456f] bg-[#0a1329] px-2 py-1.5 text-xs text-slate-100"
              >
                <option value="">Sélectionner un scénario CTA</option>
                {ctaScenarios.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    #{s.id} — {s.scenario_name || 'Scénario'}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void generateFromScenario()}
              className="rounded bg-indigo-600 px-3 py-1.5 text-xs text-white hover:bg-indigo-500"
            >
              Générer
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-slate-300">Chargement...</p>}
      {error && <p className="mb-2 text-red-300">{error}</p>}

      {!loading && tab === 'buildings' && (
        <div className="overflow-x-auto rounded border border-[#33456f]">
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr>
                <th className={thCls}>ID</th>
                <th className={thCls}>Type</th>
                <th className={thCls}>État</th>
                <th className={thCls}>Nom</th>
                <th className={thCls}>Niv.</th>
                <th className={thCls}>Lien CTA</th>
                <th className={thCls}>Capacités / postes</th>
                <th className={`${thCls} w-[1%] whitespace-nowrap`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((b: any) => {
                const cta = isCtaBuilding(b);
                const isEd = editingId === Number(b.id);
                return (
                  <React.Fragment key={b.id}>
                    <tr>
                      <td className={tdCls}>#{b.id}</td>
                      <td className={tdCls}>
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="text-base">{b.building_icon || '🏢'}</span>
                          <span>{b.building_type_name || '—'}</span>
                          {cta && (
                            <span className="rounded bg-violet-900/60 px-1.5 py-0.5 text-[9px] uppercase text-violet-200">CTA</span>
                          )}
                        </div>
                      </td>
                      <td className={tdCls}>
                        <span
                          className={`inline-flex rounded px-2 py-0.5 text-[10px] font-medium ${
                            Number(b.actif) ? 'bg-emerald-900/50 text-emerald-200' : 'bg-slate-700/80 text-slate-300'
                          }`}
                        >
                          {Number(b.actif) ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          b.nom
                        ) : (
                          <input
                            value={editForm.nom || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, nom: e.target.value }))}
                            className="w-full min-w-[140px] rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[11px] text-slate-100"
                            placeholder="Nom"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          b.niveau
                        ) : (
                          <input
                            type="number"
                            min={1}
                            value={editForm.niveau || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, niveau: e.target.value }))}
                            className="w-16 rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[11px] text-slate-100"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {cta ? (
                          <span className="text-[10px] text-slate-500">— (CTA, pas de lien vers un autre CTA)</span>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <select
                              value={buildingCtaLink[Number(b.id)] ?? String(b.cta_id || '')}
                              onChange={(e) => setBuildingCtaLink((prev) => ({ ...prev, [Number(b.id)]: e.target.value }))}
                              className="max-w-[220px] rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[10px] text-slate-100"
                            >
                              <option value="">Aucun CTA</option>
                              {ctaBuildings.map((c) => (
                                <option key={c.id} value={String(c.id)}>
                                  #{c.id} — {c.nom} ({c.actif ? 'Actif' : 'Inactif'})
                                </option>
                              ))}
                            </select>
                            <span className="text-[9px] text-slate-500">CTA lié : {b.cta_id ? `#${b.cta_id}` : 'aucun'}</span>
                          </div>
                        )}
                      </td>
                      <td className={`${tdCls} text-[10px] text-slate-400`}>
                        Pers. {b.capacite_personnel ?? 0} / Véh. {b.capacite_vehicules ?? 0} · postes {b.postes_occupes ?? 0}/{b.postes_disponibles ?? 0} · pl. {b.vehicules_places ?? 0}
                      </td>
                      <td className={tdCls}>
                        <div className="flex flex-col gap-1">
                          {!isEd ? (
                            <button
                              type="button"
                              onClick={() => startEdit(b)}
                              className="rounded bg-amber-600 px-2 py-0.5 text-[10px] text-white hover:bg-amber-500"
                            >
                              Modifier
                            </button>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              <button
                                type="button"
                                onClick={() => void handleSave(userId, b)}
                                className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] text-white"
                              >
                                Enregistrer
                              </button>
                              <button type="button" onClick={cancelEdit} className="rounded bg-slate-600 px-2 py-0.5 text-[10px] text-white">
                                Annuler
                              </button>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => void toggleBuildingActive(Number(b.id), !Number(b.actif))}
                            className={`rounded px-2 py-0.5 text-[10px] text-white ${
                              Number(b.actif) ? 'bg-amber-700 hover:bg-amber-600' : 'bg-emerald-700 hover:bg-emerald-600'
                            }`}
                          >
                            {Number(b.actif) ? 'Désactiver' : 'Activer'}
                          </button>
                          {!cta && (
                            <button
                              type="button"
                              onClick={() => void updateBuildingCta(Number(b.id), buildingCtaLink[Number(b.id)] ?? String(b.cta_id || ''))}
                              className="rounded bg-cyan-800 px-2 py-0.5 text-[10px] text-white hover:bg-cyan-700"
                            >
                              Appliquer le lien CTA
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleDelete('buildings', userId, Number(b.id))}
                            className="rounded bg-red-800 px-2 py-0.5 text-[10px] text-white hover:bg-red-700"
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={8} className="border-b border-[#263455] bg-[#080f22] px-2 py-1.5 text-[10px] text-slate-500">
                        Localisation : {b.localisation || '—'}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun bâtiment trouvé.</p>}
        </div>
      )}

      {!loading && tab === 'vehicles' && (
        <div className="overflow-x-auto rounded border border-[#33456f]">
          <table className="w-full min-w-[1000px] border-collapse text-left">
            <thead>
              <tr>
                <th className={thCls}>ID</th>
                <th className={thCls}>Type (config)</th>
                <th className={thCls}>Nom</th>
                <th className={thCls}>Statut</th>
                <th className={thCls}>Km</th>
                <th className={thCls}>Cap.</th>
                <th className={thCls}>Coût</th>
                <th className={thCls}>Prém.</th>
                <th className={thCls}>Caserne</th>
                <th className={thCls}>Détails type</th>
                <th className={`${thCls} w-[1%]`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((v: any) => {
                const isEd = editingId === Number(v.id);
                return (
                  <React.Fragment key={v.id}>
                    <tr>
                      <td className={tdCls}>#{v.id}</td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <>
                            {getVehicleIcon(v.vehicle_code)} {v.vehicle_type_name || v.vehicle_code || '—'}
                            <div className="text-[9px] text-slate-500">config #{v.config_vehicle_id}</div>
                          </>
                        ) : (
                          <select
                            value={editForm.config_vehicle_id || String(v.config_vehicle_id || '')}
                            onChange={(e) => setEditForm((f) => ({ ...f, config_vehicle_id: e.target.value }))}
                            className="w-full max-w-[220px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            {lookup.config_vehicles.length === 0 && v.config_vehicle_id != null && (
                              <option value={String(v.config_vehicle_id)}>#{v.config_vehicle_id} (chargement ref.)</option>
                            )}
                            {lookup.config_vehicles.map((cv) => (
                              <option key={cv.id} value={String(cv.id)}>
                                {cv.code} — {cv.nom}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          v.nom
                        ) : (
                          <input
                            value={editForm.nom || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, nom: e.target.value }))}
                            className="w-full min-w-[120px] rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[11px] text-slate-100"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <span className="text-slate-100">{v.vehicle_status_nom || '—'}</span>
                        ) : (
                          <select
                            value={editForm.vehicle_status_id || String(v.vehicle_status_id || '')}
                            onChange={(e) => setEditForm((f) => ({ ...f, vehicle_status_id: e.target.value }))}
                            className="w-full min-w-[140px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            {lookup.vehicle_statuses.length === 0 && v.vehicle_status_id != null && (
                              <option value={String(v.vehicle_status_id)}>#{v.vehicle_status_id} (ref.)</option>
                            )}
                            {lookup.vehicle_statuses.map((st) => (
                              <option key={st.id} value={String(st.id)}>
                                {st.nom}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          v.kilometrage ?? 0
                        ) : (
                          <input
                            type="number"
                            value={editForm.kilometrage || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, kilometrage: e.target.value }))}
                            className="w-20 rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          v.capacite
                        ) : (
                          <input
                            type="number"
                            value={editForm.capacite || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, capacite: e.target.value }))}
                            className="w-16 rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          Number(v.cout ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })
                        ) : (
                          <input
                            type="number"
                            step="0.01"
                            value={editForm.cout || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, cout: e.target.value }))}
                            className="w-24 rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          Number(v.premium) ? 'Oui' : 'Non'
                        ) : (
                          <select
                            value={editForm.premium || '0'}
                            onChange={(e) => setEditForm((f) => ({ ...f, premium: e.target.value }))}
                            className="rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            <option value="0">Non</option>
                            <option value="1">Oui</option>
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          v.batiment_nom || (v.batiment_id ? `#${v.batiment_id}` : '—')
                        ) : (
                          <select
                            value={editForm.batiment_id === undefined ? String(v.batiment_id || '') : editForm.batiment_id}
                            onChange={(e) => setEditForm((f) => ({ ...f, batiment_id: e.target.value }))}
                            className="max-w-[200px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            <option value="">Aucune</option>
                            {buildingOptions.map((bld: any) => (
                              <option key={bld.id} value={String(bld.id)}>
                                #{bld.id} {bld.nom}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={`${tdCls} text-[9px] text-slate-500`}>
                        Eau {v.capacite_eau ?? 0} / Emul. {v.capacite_emulseur ?? 0}
                        {v.specialiste ? ' · spé.' : ''} · pers. {v.capacite_personnels_min ?? 0}–{v.capacite_personnels_max ?? 0}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <button
                            type="button"
                            onClick={() => startEdit(v)}
                            className="rounded bg-amber-600 px-2 py-0.5 text-[10px] text-white"
                          >
                            Modifier
                          </button>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <button type="button" onClick={() => void handleSave(userId, v)} className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] text-white">
                              Enregistrer
                            </button>
                            <button type="button" onClick={cancelEdit} className="rounded bg-slate-600 px-2 py-0.5 text-[10px] text-white">
                              Annuler
                            </button>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleDelete('vehicles', userId, Number(v.id))}
                          className="mt-1 rounded bg-red-800 px-2 py-0.5 text-[10px] text-white"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun véhicule trouvé.</p>}
        </div>
      )}

      {!loading && tab === 'staff' && (
        <div className="overflow-x-auto rounded border border-[#33456f]">
          <table className="w-full min-w-[960px] border-collapse text-left">
            <thead>
              <tr>
                <th className={thCls}>ID</th>
                <th className={thCls}>Identité</th>
                <th className={thCls}>Grade</th>
                <th className={thCls}>Statut général</th>
                <th className={thCls}>Planning</th>
                <th className={thCls}>Caserne</th>
                <th className={thCls}>Actif</th>
                <th className={thCls}>Groupe</th>
                <th className={`${thCls} w-[1%]`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((s: any) => {
                const isEd = editingId === Number(s.id);
                return (
                  <React.Fragment key={s.id}>
                    <tr>
                      <td className={tdCls}>#{s.id}</td>
                      <td className={tdCls}>
                        <div className="flex items-center gap-1">
                          <span>{getStaffIcon(s.personnel_grade_code)}</span>
                          {!isEd ? (
                            <span>
                              {s.prenom} {s.nom}
                            </span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <input
                                value={editForm.prenom || ''}
                                onChange={(e) => setEditForm((f) => ({ ...f, prenom: e.target.value }))}
                                placeholder="Prénom"
                                className="rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                              />
                              <input
                                value={editForm.nom || ''}
                                onChange={(e) => setEditForm((f) => ({ ...f, nom: e.target.value }))}
                                placeholder="Nom"
                                className="rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <>
                            {s.personnel_grade || s.personnel_grade_code || '—'}
                            <div className="text-[9px] text-slate-500">config #{s.config_personnel_id}</div>
                          </>
                        ) : (
                          <select
                            value={editForm.config_personnel_id || String(s.config_personnel_id || '')}
                            onChange={(e) => setEditForm((f) => ({ ...f, config_personnel_id: e.target.value }))}
                            className="w-full max-w-[200px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            {lookup.config_personnel.length === 0 && s.config_personnel_id != null && (
                              <option value={String(s.config_personnel_id)}>#{s.config_personnel_id} (ref.)</option>
                            )}
                            {lookup.config_personnel.map((g) => (
                              <option key={g.id} value={String(g.id)}>
                                {g.grade} ({g.grade_code})
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <span className="text-slate-100">{s.staff_status_nom || '—'}</span>
                        ) : (
                          <select
                            value={editForm.staff_status_code || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, staff_status_code: e.target.value }))}
                            className="w-full min-w-[140px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            {lookup.vehicle_statuses.length === 0 && (
                              <option value="">(chargement statuts)</option>
                            )}
                            {lookup.vehicle_statuses.map((st) => (
                              <option key={st.id} value={String(st.code || '').toLowerCase()}>
                                {st.nom}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          s.planning_statut_nom || '—'
                        ) : (
                          <select
                            value={editForm.planning_statut_nom || String(s.planning_statut_nom || '')}
                            onChange={(e) => setEditForm((f) => ({ ...f, planning_statut_nom: e.target.value }))}
                            className="w-full min-w-[180px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            {lookup.planning_statuses.length === 0 && (
                              <option value={String(s.planning_statut_nom || '')}>
                                {String(s.planning_statut_nom || 'PERSONNELS')}
                              </option>
                            )}
                            {lookup.planning_statuses.map((ps) => (
                              <option key={ps} value={ps}>
                                {ps}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          s.batiment_nom || (s.batiment_id ? `#${s.batiment_id}` : '—')
                        ) : (
                          <select
                            value={editForm.batiment_id === undefined ? String(s.batiment_id || '') : editForm.batiment_id}
                            onChange={(e) => setEditForm((f) => ({ ...f, batiment_id: e.target.value }))}
                            className="max-w-[200px] rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            <option value="">Aucune</option>
                            {buildingOptions.map((bld: any) => (
                              <option key={bld.id} value={String(bld.id)}>
                                #{bld.id} {bld.nom}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          Number(s.actif) ? 'Oui' : 'Non'
                        ) : (
                          <select
                            value={editForm.actif || '1'}
                            onChange={(e) => setEditForm((f) => ({ ...f, actif: e.target.value }))}
                            className="rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[10px] text-slate-100"
                          >
                            <option value="1">Oui</option>
                            <option value="0">Non</option>
                          </select>
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          s.groupe || '—'
                        ) : (
                          <input
                            value={editForm.groupe || ''}
                            onChange={(e) => setEditForm((f) => ({ ...f, groupe: e.target.value }))}
                            className="w-16 rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[11px] text-slate-100"
                            placeholder="A–D"
                          />
                        )}
                      </td>
                      <td className={tdCls}>
                        {!isEd ? (
                          <button type="button" onClick={() => startEdit(s)} className="rounded bg-amber-600 px-2 py-0.5 text-[10px] text-white">
                            Modifier
                          </button>
                        ) : (
                          <div className="flex flex-col gap-1">
                            <button type="button" onClick={() => void handleSave(userId, s)} className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] text-white">
                              Enregistrer
                            </button>
                            <button type="button" onClick={cancelEdit} className="rounded bg-slate-600 px-2 py-0.5 text-[10px] text-white">
                              Annuler
                            </button>
                          </div>
                        )}
                        <div className="mt-1 text-[9px] text-slate-500">Prém. {Number(s.premium) ? 'oui' : 'non'}</div>
                        {isEd && (
                          <select
                            value={editForm.premium || '0'}
                            onChange={(e) => setEditForm((f) => ({ ...f, premium: e.target.value }))}
                            className="mt-1 rounded border border-[#33456f] bg-[#0a1329] px-1 py-0.5 text-[9px] text-slate-100"
                          >
                            <option value="0">Prém. non</option>
                            <option value="1">Prém. oui</option>
                          </select>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleDelete('staff', userId, Number(s.id))}
                          className="mt-1 rounded bg-red-800 px-2 py-0.5 text-[10px] text-white"
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && <p className="p-3 text-xs text-slate-400">Aucun personnel trouvé.</p>}
        </div>
      )}

      {!loading && tab === 'interventions' && (
        <div className="overflow-x-auto rounded border border-[#33456f]">
          <table className="w-full min-w-[640px] border-collapse text-left">
            <thead>
              <tr>
                <th className={thCls}>N° / ID</th>
                <th className={thCls}>Adresse</th>
                <th className={thCls}>Statut</th>
                <th className={`${thCls} w-[1%]`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((i: any) => {
                const isEd = editingId === Number(i.id);
                return (
                  <tr key={i.id}>
                    <td className={tdCls}>
                      {!isEd ? (
                        <span>
                          {i.numero_intervention} <span className="text-slate-500">(#{i.id})</span>
                        </span>
                      ) : (
                        <input
                          value={editForm.numero_intervention || ''}
                          onChange={(e) => setEditForm((f) => ({ ...f, numero_intervention: e.target.value }))}
                          className="w-full max-w-[200px] rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[11px] text-slate-100"
                        />
                      )}
                    </td>
                    <td className={tdCls}>
                      {!isEd ? (
                        i.adresse || '—'
                      ) : (
                        <input
                          value={editForm.adresse || ''}
                          onChange={(e) => setEditForm((f) => ({ ...f, adresse: e.target.value }))}
                          className="w-full min-w-[240px] rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 text-[11px] text-slate-100"
                        />
                      )}
                    </td>
                    <td className={tdCls}>
                      {!isEd ? (
                        <span className="rounded bg-[#1a2545] px-1.5 py-0.5 font-mono text-[10px]">{i.statut || '—'}</span>
                      ) : (
                        <input
                          value={editForm.statut || ''}
                          onChange={(e) => setEditForm((f) => ({ ...f, statut: e.target.value.toUpperCase() }))}
                          className="w-32 rounded border border-[#33456f] bg-[#0a1329] px-1.5 py-0.5 font-mono text-[11px] text-slate-100"
                        />
                      )}
                    </td>
                    <td className={tdCls}>
                      <div className="flex flex-col gap-1">
                        {!isEd ? (
                          <button type="button" onClick={() => startEdit(i)} className="rounded bg-amber-600 px-2 py-0.5 text-[10px] text-white">
                            Modifier
                          </button>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            <button type="button" onClick={() => void handleSave(userId, i)} className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] text-white">
                              Enregistrer
                            </button>
                            <button type="button" onClick={cancelEdit} className="rounded bg-slate-600 px-2 py-0.5 text-[10px] text-white">
                              Annuler
                            </button>
                          </div>
                        )}
                        <button type="button" onClick={() => void resetIntervention(Number(i.id))} className="rounded bg-sky-800 px-2 py-0.5 text-[10px] text-white">
                          Reset
                        </button>
                        <button type="button" onClick={() => void cancelIntervention(Number(i.id))} className="rounded bg-orange-800 px-2 py-0.5 text-[10px] text-white">
                          Annuler int.
                        </button>
                        <button type="button" onClick={() => void handleDelete('interventions', userId, Number(i.id))} className="rounded bg-red-800 px-2 py-0.5 text-[10px] text-white">
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredItems.length === 0 && <p className="p-3 text-xs text-slate-400">Aucune intervention trouvée.</p>}
        </div>
      )}

      {tab === 'buildings' && possessionData && (
        <div className="mt-4 rounded border border-[#33456f] bg-[#101f44] p-3">
          <div className="mb-2 text-sm font-semibold text-white">Détail par bâtiment (véhicules & personnel rattachés)</div>
          {filteredItems.length === 0 && <p className="text-xs text-slate-400">Aucun bâtiment trouvé.</p>}
          {filteredItems.map((b: any) => (
            <details key={`panel-${b.id}`} className="mb-2 rounded border border-[#2b3a61] bg-[#0a1329] px-2 py-1">
              <summary className="cursor-pointer text-xs text-slate-100">
                {b.building_icon || '🏢'} #{b.id} — {b.nom} · {Number(b.actif) ? 'Actif' : 'Inactif'} · {b.building_type_name || '—'}
              </summary>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                <LinkedListByBuilding
                  buildingId={Number(b.id)}
                  vehicles={possessionData.vehicles}
                  staff={possessionData.staff}
                  type="vehicles"
                />
                <LinkedListByBuilding
                  buildingId={Number(b.id)}
                  vehicles={possessionData.vehicles}
                  staff={possessionData.staff}
                  type="staff"
                />
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Localisation : {b.localisation || '—'} · postes {b.postes_occupes ?? 0}/{b.postes_disponibles ?? 0} · véh. placés {b.vehicules_places ?? 0}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

const LinkedListByBuilding: React.FC<{
  buildingId: number;
  type: 'vehicles' | 'staff';
  vehicles: any[];
  staff: any[];
}> = ({ buildingId, type, vehicles, staff }) => {
  const rows = type === 'vehicles' ? vehicles.filter((r) => Number(r.batiment_id || 0) === buildingId) : staff.filter((r) => Number(r.batiment_id || 0) === buildingId);

  return (
    <div className="rounded border border-[#33456f] bg-[#101f44] p-2">
      <div className="mb-1 text-xs font-semibold text-white">{type === 'vehicles' ? 'Véhicules liés' : 'Personnel lié'}</div>
      {rows.length === 0 && <p className="text-[11px] text-slate-400">Aucun élément lié.</p>}
      {rows.length > 0 && (
        <ul className="list-inside list-disc space-y-0.5 text-[11px] text-slate-200">
          {rows.map((r: any) => (
            <li key={r.id}>
              #{r.id}{' '}
              {type === 'vehicles' ? (
                <span>
                  {r.nom} <span className="text-slate-500">({r.vehicle_type_name || r.vehicle_code || 'type'})</span>
                </span>
              ) : (
                <span>
                  {r.prenom} {r.nom} <span className="text-slate-500">({r.personnel_grade || r.personnel_grade_code || 'grade'})</span>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PanelModerationManageV1;
